import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { createAuthService } from '../services/authService';
import { initializeFirebase, getFirebaseInstance } from '../firebase/config';
import { transactionService } from '../services/transactionService';
import logoImg from '../img/newlogo.png';
import logoenblanco from '../img/logoenblanco.png';
import OperatorsModal from '../components/OperatorsModal';
import ProfileModal from '../components/ProfileModal';
import WelcomeAdminModal from '../components/WelcomeAdminModal';
import ConfirmModal from '../components/ConfirmModal';
import HelpModal from '../components/HelpModal';
import VistaComoBar from '../components/VistaComoBar';
import AliasCuenta from '../components/AliasCuenta';
import BajaUsuarioModal from '../components/BajaUsuarioModal';
import { adminService } from '../services/adminService';
import { useRecargaConVersionNueva } from '../utils/versionNueva';
import mpIcono from '../img/mp-icono.png';

// De dónde vino la transferencia, con el mismo criterio que apiBuscarTransferencias:
// INTRA_PSP es desde otra cuenta de Mercado Pago; INTER_PSP, desde un banco u otra
// billetera. Mercado Pago no informa el nombre de quien transfiere, solo su CUIL, y
// ese número no llega al navegador: el backend lo cambia por el nombre del padrón de
// ARCA (`pagador_nombre`) o, mientras no lo tiene, por el documento tapado
// (`pagador_documento`, ver functions/src/transactions/pagador.js). En las que vienen
// de un banco no hay ni eso (Mercado Pago informa el CUIT de la cuenta que recibe).
const origenDeTransferencia = (transaction) => {
    const subtipo = transaction.point_of_interaction?.sub_type;
    if (subtipo === 'INTRA_PSP') {
        const nombre = transaction.pagador_nombre || null;
        return {
            tipo: 'mercadopago',
            nombre,
            documento: nombre ? null : transaction.pagador_documento || null
        };
    }
    if (subtipo === 'INTER_PSP') return { tipo: 'banco' };
    return null;
};

const BankIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 10l9-6 9 6"></path>
        <line x1="5" y1="10" x2="5" y2="18"></line>
        <line x1="9.5" y1="10" x2="9.5" y2="18"></line>
        <line x1="14.5" y1="10" x2="14.5" y2="18"></line>
        <line x1="19" y1="10" x2="19" y2="18"></line>
        <line x1="3" y1="20" x2="21" y2="20"></line>
    </svg>
);

const TransferOrigin = ({ transaction }) => {
    const origen = origenDeTransferencia(transaction);
    if (!origen) return null;

    if (origen.tipo === 'mercadopago') {
        return (
            <div className="transaction-origin origin-mp">
                <img src={mpIcono} alt="" className="origin-logo" />
                <div className="origin-text">
                    <span className="origin-label">{origen.nombre || 'Desde Mercado Pago'}</span>
                    {origen.documento && <span className="origin-detail">{origen.documento}</span>}
                </div>
            </div>
        );
    }

    return (
        <div className="transaction-origin origin-banco">
            <span className="origin-logo origin-logo-banco"><BankIcon /></span>
            <div className="origin-text">
                <span className="origin-label">Desde cuenta bancaria</span>
            </div>
        </div>
    );
};

// Componente de búsqueda memoizado
const SearchInput = memo(({ value, onChange }) => {
    const handleChange = (e) => {
        const newValue = e.target.value;
        if (newValue === '' || (/^\d{0,6}$/.test(newValue))) {
            onChange(newValue);
        }
    };

    return (
        <div className="search-container">
            <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Buscar por monto..."
                value={value}
                onChange={handleChange}
                className="search-input"
                maxLength="6"
                autoComplete="off"
            />
        </div>
    );
});

// Componente de tabla memoizado
const TransactionsList = memo(({
    todayTransactions,
    yesterdayTransactions,
    searchValue,
    onSearchChange,
    highlightedTransactions,
    limite,
    alias,
    titular,
    mostrarAlias,
    onBusqueda,
    onCambioAlias
}) => {
    const filterTransactionsByAmount = (transactions, searchValue) => {
        if (!searchValue) return transactions;
        return transactions.filter(transaction => {
            const amount = Math.floor(transaction.transaction_amount).toString();
            return amount.includes(searchValue.trim());
        });
    };

    const filteredTransactions = filterTransactionsByAmount([...todayTransactions, ...yesterdayTransactions], searchValue);

    // Para medir el uso del buscador (scripts/uso.js): una búsqueda es un monto que
    // quedó quieto un momento, no cada tecla. Se anota cuántos dígitos tenía y
    // cuántas transferencias mostró.
    const resultadosRef = useRef(0);
    resultadosRef.current = filteredTransactions.length;
    useEffect(() => {
        if (!searchValue || !onBusqueda) return undefined;
        const pausa = setTimeout(() => {
            onBusqueda({ digitos: searchValue.length, resultados: resultadosRef.current });
        }, 1500);
        return () => clearTimeout(pausa);
    }, [searchValue, onBusqueda]);

    return (
        <>
            <SearchInput
                value={searchValue}
                onChange={onSearchChange}
            />
            <div className="transactions-grid">
                {alias && mostrarAlias && <AliasCuenta alias={alias} titular={titular} onCambio={onCambioAlias} />}
                <div className="transactions-header">
                    Transferencias recibidas en las últimas 24hs
                    {limite && (
                        <div className="transactions-limit">
                            Se muestran las de hasta ${Number(limite).toLocaleString('es-AR')}
                        </div>
                    )}
                </div>
                {filteredTransactions.length === 0 ? (
                    <div className="transaction-card">
                        <p>No hay transacciones para mostrar</p>
                    </div>
                ) : (
                    filteredTransactions.map((transaction) => (
                        <div
                            key={transaction.id}
                            className={`transaction-card ${highlightedTransactions.has(transaction.id) ? 'highlight-new' : ''}`}
                        >
                            <div className="transaction-main">
                                <div className="transaction-details">
                                    <div className="transaction-date">
                                        {new Date(transaction.date_approved).toLocaleDateString('es-AR')}
                                    </div>
                                    <div className="transaction-time">
                                        {new Date(transaction.date_approved).toLocaleTimeString('es-AR', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit',
                                            hour12: false
                                        })}
                                    </div>
                                </div>
                                <TransferOrigin transaction={transaction} />
                            </div>
                            <div className="transaction-amount">
                                ${Math.floor(transaction.transaction_amount).toLocaleString('es-AR')}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </>
    );
});

// Componentes de iconos
const HelpIcon = () => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <circle cx="12" cy="12" r="10"></circle>
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
        <line x1="12" y1="17" x2="12" y2="17"></line>
    </svg>
);

const OperatorsIcon = () => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
);

const WalletIcon = () => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
        <path d="M18 12h-2v4h2v-4z"></path>
    </svg>
);

const ProfileIcon = () => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
    </svg>
);

// Añadir el nuevo componente StoreIcon después de los otros iconos
const StoreIcon = () => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        <polyline points="9 22 9 12 15 12 15 22"></polyline>
    </svg>
);

// Componente para el menú hamburguesa
const MenuToggle = ({ onClick }) => (
    <button className="menu-toggle" onClick={onClick} aria-label="Menu">
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <line x1="4" y1="12" x2="20" y2="12"></line>
            <line x1="4" y1="6" x2="20" y2="6"></line>
            <line x1="4" y1="18" x2="20" y2="18"></line>
        </svg>
    </button>
);

// Componente para la barra de navegación
const NavigationBar = ({ onLogout, onOperatorsClick, onProfileClick, isAdmin }) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    return (
        <>
            <MenuToggle onClick={toggleSidebar} />
            <div className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
                <div className="nav-icon" title="Ayuda">
                    <HelpIcon />
                </div>
                {isAdmin && (
                    <>
                        <div className="nav-icon" title="Operadores" onClick={onOperatorsClick}>
                            <OperatorsIcon />
                        </div>
                        <div className="nav-icon" title="Facturación y pagos">
                            <WalletIcon />
                        </div>
                    </>
                )}
                <div className="nav-icon" title="Mi perfil" onClick={onProfileClick}>
                    <ProfileIcon />
                </div>
            </div>
        </>
    );
};

// Componente para el header
const Header = ({ onLogout, onOperatorsClick, onProfileClick, onHelpClick, isAdmin, currentUser }) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // Verificar si el usuario es un operador con email_administrador
    const isRestrictedOperator = !currentUser?.admin && currentUser?.email_administrador;

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const closeSidebar = () => {
        setIsSidebarOpen(false);
    };

    const handleItemClick = (callback) => {
        closeSidebar();
        if (callback) {
            callback();
        }
    };

    return (
        <>
            <div className="header-container">
                <div className="header-left">
                    <button className="menu-toggle" onClick={toggleSidebar}>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <line x1="3" y1="12" x2="21" y2="12"></line>
                            <line x1="3" y1="6" x2="21" y2="6"></line>
                            <line x1="3" y1="18" x2="21" y2="18"></line>
                        </svg>
                    </button>
                    <img src={logoenblanco} alt="Logo" className="logo" />
                </div>
                <div className="header-right">
                    <div className="business-name">
                        <StoreIcon />
                        <span>{currentUser?.businessName || 'Empresa'}</span>
                    </div>
                    <div className="user-profile" onClick={onProfileClick}>
                        <ProfileIcon />
                        <span className="user-name">{currentUser?.fullName || 'Usuario'}</span>
                    </div>
                    <div className="header-actions">
                        <div className="nav-item" onClick={onHelpClick} data-tooltip="Ayuda">
                            <HelpIcon />
                        </div>
                        {!isRestrictedOperator && isAdmin && (
                            <div className="nav-item" onClick={onOperatorsClick} data-tooltip="Operadores">
                                <OperatorsIcon />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
                <div className="sidebar-header">
                    <div className="business-name">
                        {currentUser?.businessName || 'Empresa'}
                    </div>
                    <button className="sidebar-close" onClick={closeSidebar}>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>
                <div className="sidebar-content">
                    <div className="sidebar-item" onClick={() => handleItemClick(onProfileClick)}>
                        <ProfileIcon />
                        <span>{currentUser?.fullName || 'Usuario'}</span>
                    </div>
                    <div className="sidebar-item" onClick={() => handleItemClick(onHelpClick)}>
                        <HelpIcon />
                        <span>Ayuda</span>
                    </div>
                    {!isRestrictedOperator && isAdmin && (
                        <div className="sidebar-item" onClick={() => handleItemClick(onOperatorsClick)}>
                            <OperatorsIcon />
                            <span>Operadores</span>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

function Home() {
    const [authService, setAuthService] = useState(null);
    const [authState, setAuthState] = useState(null);
    const [userCollectorId, setUserCollectorId] = useState(null);
    const [resultMessage, setResultMessage] = useState(null);
    const [todayTransactions, setTodayTransactions] = useState([]);
    const [yesterdayTransactions, setYesterdayTransactions] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [showOperatorsModal, setShowOperatorsModal] = useState(false);
    const [highlightedTransactions, setHighlightedTransactions] = useState(new Set());
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isPrimaryAdmin, setIsPrimaryAdmin] = useState(false);
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);
    const [firstLogin, setFirstLogin] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [showHelpModal, setShowHelpModal] = useState(false);
    // Monto máximo que ve este usuario: lo manda el backend a los operadores de una
    // cuenta con límite (adminFijarLimite). null = ve todas.
    const [limiteMonto, setLimiteMonto] = useState(null);
    // Alias de la cuenta de MP (adminFijarAlias): lo ven todos los usuarios de la cuenta.
    const [aliasCuenta, setAliasCuenta] = useState(null);
    // Titular de la cuenta (padrón de ARCA), debajo del alias en el cartel.
    const [titularCuenta, setTitularCuenta] = useState(null);
    // "Mostrar alias en el panel" de Mi Perfil: prendido salvo que un admin lo apague.
    const [mostrarAlias, setMostrarAlias] = useState(true);
    const navigate = useNavigate();
    const notificationTimeoutRef = useRef(null);
    // Cuándo llegó la última transferencia nueva, para no recargar por una versión nueva
    // del front mientras el cajero la está mirando.
    const ultimaTransferenciaNuevaRef = useRef(0);

    // Vista por usuario del superadmin (claim de scripts/hacer-superadmin.js): ve el
    // home como lo ve el usuario elegido, en solo lectura. `verComo` es su email, o
    // null para la cuenta propia.
    const [esSuperadmin, setEsSuperadmin] = useState(false);
    const [negocios, setNegocios] = useState([]);
    const [cargandoNegocios, setCargandoNegocios] = useState(false);
    const [errorNegocios, setErrorNegocios] = useState(null);
    const [verComo, setVerComo] = useState(() => adminService.leerVerComo());
    const [bajaPendiente, setBajaPendiente] = useState(null);

    const verComoActivo = esSuperadmin ? verComo : null;
    const { usuarioVista, negocioVista, operadoresVista, limiteCuentaVista } = useMemo(() => {
        if (!verComoActivo) return {};
        const negocio = negocios.find(n => n.usuarios.some(u => u.email === verComoActivo));
        const usuario = negocio?.usuarios.find(u => u.email === verComoActivo) || null;
        return {
            usuarioVista: usuario,
            negocioVista: negocio?.nombre || null,
            operadoresVista: usuario ? negocio.usuarios.filter(u => u.email_administrador === usuario.email) : [],
            limiteCuentaVista: negocio?.limite_operadores || null
        };
    }, [negocios, verComoActivo]);

    // Mientras se mira a otro, todo el home se arma con sus datos. Si todavía no
    // cargó la lista, no se consulta nada.
    const usuarioMostrado = verComoActivo ? usuarioVista : authState;
    const collectorVista = verComoActivo ? usuarioVista?.collector_id || null : userCollectorId;
    const isAdminVista = verComoActivo ? usuarioVista?.admin === true : isAdmin;

    const cambiarVista = useCallback((email) => {
        adminService.guardarVerComo(email);
        setVerComo(email);
        setSearchValue('');
    }, []);

    const cargarNegocios = useCallback(async () => {
        setCargandoNegocios(true);
        setErrorNegocios(null);
        try {
            setNegocios(await adminService.listarNegocios());
        } catch (error) {
            console.error('Error al cargar los usuarios:', error);
            setErrorNegocios(error.message);
        } finally {
            setCargandoNegocios(false);
        }
    }, []);

    const fijarLimite = useCallback(async (collectorId, limite) => {
        await adminService.fijarLimite(collectorId, limite);
        await cargarNegocios();
    }, [cargarNegocios]);

    // El admin de la cuenta lo cambia desde Mi Perfil: se muestra al toque, sin esperar
    // a la próxima consulta de transferencias.
    const handleAlias = useCallback((alias, titular, mostrar) => {
        setAliasCuenta(alias);
        setTitularCuenta(titular);
        setMostrarAlias(mostrar);
    }, []);

    const guardarAliasDeLaCuenta = useCallback(async (alias) => {
        const cuenta = await adminService.fijarAliasDeLaCuenta(alias);
        setAliasCuenta(cuenta.alias ?? null);
        setMostrarAlias(cuenta.mostrar !== false);
    }, []);

    const cambiarMostrarAlias = useCallback(async (mostrar) => {
        const cuenta = await adminService.mostrarAliasDeLaCuenta(mostrar);
        setMostrarAlias(cuenta.mostrar !== false);
    }, []);

    const fijarAlias = useCallback(async (collectorId, alias) => {
        await adminService.fijarAlias(collectorId, alias);
        await cargarNegocios();
    }, [cargarNegocios]);

    // Se actualiza solo ese negocio con lo que devuelve el backend: recargar la lista
    // entera haría parpadear el panel en cada botón.
    const accionSuscripcion = useCallback(async (collectorId, accion, datos) => {
        const suscripcion = await adminService.suscripcion(collectorId, accion, datos);
        setNegocios((actuales) => actuales.map((n) => (n.collector_id === collectorId ? { ...n, suscripcion } : n)));
    }, []);

    const handleSearch = useCallback((value) => {
        setSearchValue(value);
    }, []);

    const handleBusqueda = useCallback((busqueda) => {
        transactionService.registrarBusqueda(busqueda);
    }, []);

    const handleCambioAlias = useCallback((oculto) => {
        transactionService.registrarAlias(oculto ? 'ocultar' : 'mostrar');
    }, []);

    const handleNewTransactions = useCallback((newTransactions) => {
        ultimaTransferenciaNuevaRef.current = Date.now();

        // Actualizar las transacciones
        setTodayTransactions(prevTransactions => {
            const today = new Date().toDateString();
            const newToday = newTransactions.filter(t =>
                new Date(t.date_approved).toDateString() === today
            );
            return [...newToday, ...prevTransactions];
        });

        setYesterdayTransactions(prevTransactions => {
            const yesterday = new Date(Date.now() - 86400000).toDateString();
            const newYesterday = newTransactions.filter(t =>
                new Date(t.date_approved).toDateString() === yesterday
            );
            return [...newYesterday, ...prevTransactions];
        });

        // Marcar las nuevas transacciones para el efecto visual
        const newIds = new Set(newTransactions.map(t => t.id));
        setHighlightedTransactions(newIds);

        // Remover el efecto visual después de 3 segundos
        setTimeout(() => {
            setHighlightedTransactions(new Set());
        }, 3000);
    }, []);

    // Cambió el límite de monto con la pantalla abierta: se vacía la lista y el
    // servicio la vuelve a llenar con lo que mandó el backend (ver transactionService).
    const handleLimite = useCallback((limite, rearmar) => {
        setLimiteMonto(limite);
        if (rearmar) {
            setTodayTransactions([]);
            setYesterdayTransactions([]);
        }
    }, []);

    // Transferencias que ya estaban en pantalla y cambiaron (por ejemplo, llegó el
    // nombre de quien transfirió): se reemplazan en el lugar, sin el efecto de nueva.
    const handleUpdatedTransactions = useCallback((updated) => {
        const porId = new Map(updated.map(t => [t.id, t]));
        const reemplazar = (lista) => lista.map(t => porId.get(t.id) || t);
        setTodayTransactions(reemplazar);
        setYesterdayTransactions(reemplazar);
    }, []);

    const showNotification = useCallback((message, status) => {
        // Limpiar cualquier timeout existente
        if (notificationTimeoutRef.current) {
            clearTimeout(notificationTimeoutRef.current);
        }

        setResultMessage({ message, status });

        // Configurar un nuevo timeout para ocultar la notificación después de 5 segundos
        notificationTimeoutRef.current = setTimeout(() => {
            setResultMessage(null);
        }, 5000);
    }, []);

    const getRecentTransactions = useCallback(async () => {
        try {
            // Verificar si tenemos la información necesaria
            if (!collectorVista) {
                return;
            }

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);

            const endDate = new Date();
            const startDate = yesterday;

            // Intentar obtener las transacciones
            await transactionService.getTransactions(
                null,
                startDate,
                endDate,
                collectorVista,
                {
                    onNewTransactions: handleNewTransactions,
                    onUpdatedTransactions: handleUpdatedTransactions,
                    onLimite: handleLimite,
                    onAlias: handleAlias
                }
            );

            // Si llegamos aquí, la operación fue exitosa
            // Limpiar cualquier mensaje de error previo relacionado con transacciones
            if (resultMessage &&
                resultMessage.status === 'not-found' &&
                (resultMessage.message === 'Error al obtener las transacciones.' ||
                    resultMessage.message === 'Error al obtener las transacciones')) {
                setResultMessage(null);
            }
        } catch (error) {
            console.error('Error al obtener transacciones:', error);

            // Filtrar mensajes de error que no deberían mostrarse al usuario
            const errorMessage = error.message || 'Error al obtener las transacciones';
            const shouldShowError =
                errorMessage !== 'No hay usuario autenticado' &&
                !errorMessage.includes('token') &&
                !errorMessage.includes('auth') &&
                !errorMessage.includes('Token');

            // Solo mostrar errores relevantes para el usuario
            if (shouldShowError) {
                // Evitar mostrar el mismo error repetidamente
                if (!resultMessage ||
                    resultMessage.message !== errorMessage) {
                    showNotification(errorMessage, "not-found");
                }
            }
        }
    }, [collectorVista, handleNewTransactions, handleUpdatedTransactions, handleLimite, showNotification, resultMessage]);

    // Función para manejar el intento de cierre de sesión
    const handleLogoutAttempt = () => {
        setShowLogoutConfirm(true);
    };

    // Función para confirmar el cierre de sesión
    const handleLogoutConfirm = () => {
        adminService.guardarVerComo(null);
        authService.signOutUser();
        setShowLogoutConfirm(false);
    };

    useEffect(() => {
        const initializeServices = async () => {
            try {
                console.log('Iniciando servicios...');
                const { auth } = await initializeFirebase();
                console.log('Firebase inicializado');

                const authService = await createAuthService();
                setAuthService(authService);

                // Configurar el listener de autenticación
                const unsubscribe = onAuthStateChanged(auth, async (user) => {
                    console.log('Estado de autenticación cambiado:', user ? 'autenticado' : 'no autenticado');
                    if (user) {
                        setAuthState(user);
                    } else {
                        setAuthState(null);
                        navigate('/login');
                    }
                });

                return () => unsubscribe();
            } catch (error) {
                console.error('Error al inicializar servicios:', error);
                navigate('/login');
            }
        };

        initializeServices();
    }, [navigate]);

    useEffect(() => {
        if (!authService) return;

        const { auth } = getFirebaseInstance();
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    const userData = await authService.getUserData(user.email);
                    setAuthState({
                        ...user,
                        ...userData
                    });
                    setUserCollectorId(userData?.collector_id);
                    setIsAdmin(userData?.admin === true);
                    adminService.esSuperadmin(user).then(setEsSuperadmin);

                    // Link directo de los mails (/home?operadores): abre la gestión de operadores.
                    if (userData?.admin === true && new URLSearchParams(window.location.search).has('operadores')) {
                        setShowOperatorsModal(true);
                        window.history.replaceState(null, '', window.location.pathname);
                    }

                    // Verificar si es administrador principal (tiene mp_email)
                    const isPrimary = !!userData?.mp_email;
                    setIsPrimaryAdmin(isPrimary);

                    // Verificar si es la primera vez que inicia sesión
                    // Usamos la fecha de creación para determinar si es reciente
                    if (isPrimary) {
                        const createdAt = userData?.createdAt || userData?.tokenUpdatedAt?.toDate?.();
                        const now = new Date();

                        // Si no hay fecha de último login o es la primera vez que inicia sesión
                        if (!userData?.lastLogin) {
                            setFirstLogin(true);
                            setShowWelcomeModal(true);

                            // Actualizar el documento para marcar que ya inició sesión
                            try {
                                await authService.updateUserData(user.email, {
                                    lastLogin: now.toISOString()
                                });
                            } catch (error) {
                                console.error('Error al actualizar lastLogin:', error);
                            }
                        }
                    }

                    setResultMessage(null);
                    if (userData?.listado) {
                        await getRecentTransactions();
                    }
                } catch (error) {
                    console.error('Error al obtener datos del usuario:', error);
                    showNotification('Error al obtener datos del usuario.', 'not-found');
                }
            } else {
                setAuthState(null);
                setUserCollectorId(null);
                setIsAdmin(false);
                setEsSuperadmin(false);
                navigate('/login');
            }
        });

        return () => unsubscribe();
    }, [authService, navigate, showNotification]);

    useEffect(() => {
        if (esSuperadmin) {
            cargarNegocios();
        }
    }, [esSuperadmin, cargarNegocios]);

    // Si el usuario que se estaba mirando ya no existe (por ejemplo, se le dio de
    // baja), se vuelve a la cuenta propia.
    useEffect(() => {
        if (verComoActivo && !cargandoNegocios && !errorNegocios && negocios.length > 0 && !usuarioVista) {
            cambiarVista(null);
        }
    }, [verComoActivo, cargandoNegocios, errorNegocios, negocios, usuarioVista, cambiarVista]);

    // Al cambiar de vista se empieza de cero: las transferencias en pantalla son
    // de la vista anterior. Tiene que correr antes que el polling de abajo.
    useEffect(() => {
        transactionService.setVerComo(verComoActivo);
        setTodayTransactions([]);
        setYesterdayTransactions([]);
        setHighlightedTransactions(new Set());
        setLimiteMonto(null);
        setAliasCuenta(null);
        setTitularCuenta(null);
        setMostrarAlias(true);
    }, [verComoActivo]);

    useEffect(() => {
        let intervalId;

        const startPolling = () => {
            // Realizar la primera consulta inmediatamente
            getRecentTransactions();

            // Configurar el intervalo para consultas posteriores
            intervalId = setInterval(() => {
                getRecentTransactions();
            }, 2000);
        };

        if (collectorVista) {
            startPolling();
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [collectorVista, getRecentTransactions]);

    // Hay una versión nueva del front publicada: se recarga sola, pero no con una ventana
    // abierta, ni con un monto en el buscador o una transferencia de hace menos de un minuto
    // a la vista. Con la pestaña oculta da igual el buscador (ver utils/versionNueva.js).
    // Mientras espera, un aviso permite actualizar en el momento.
    const modalAbierto = showOperatorsModal || showProfileModal || showHelpModal ||
        showWelcomeModal || showLogoutConfirm || !!bajaPendiente;
    const { hayVersionNueva, recargar: recargarVersionNueva } = useRecargaConVersionNueva(() =>
        modalAbierto ||
        (document.visibilityState === 'visible' &&
            (searchValue !== '' || Date.now() - ultimaTransferenciaNuevaRef.current < 60 * 1000))
    );

    useEffect(() => {
        // Limpiar el timeout cuando el componente se desmonte
        return () => {
            if (notificationTimeoutRef.current) {
                clearTimeout(notificationTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className="container">
            {authState && (
                <Header
                    onLogout={handleLogoutAttempt}
                    onOperatorsClick={() => setShowOperatorsModal(true)}
                    onProfileClick={() => setShowProfileModal(true)}
                    onHelpClick={() => setShowHelpModal(true)}
                    isAdmin={isAdminVista}
                    currentUser={usuarioMostrado}
                />
            )}

            {esSuperadmin && (
                <VistaComoBar
                    negocios={negocios}
                    cargando={cargandoNegocios}
                    error={errorNegocios}
                    miEmail={authState?.email}
                    usuarioVista={usuarioVista}
                    negocioVista={negocioVista}
                    onElegir={cambiarVista}
                    onBaja={setBajaPendiente}
                    onRecargar={cargarNegocios}
                    onFijarLimite={fijarLimite}
                    onFijarAlias={fijarAlias}
                    onSuscripcion={accionSuscripcion}
                />
            )}

            {resultMessage && (
                <div className={`notification ${resultMessage.status}`}>
                    {resultMessage.status === 'approved' ? (
                        <span role="img" aria-label="success">✅ </span>
                    ) : (
                        <span role="img" aria-label="error">⚠️ </span>
                    )}
                    {resultMessage.message}
                </div>
            )}

            {hayVersionNueva && (
                <div className="aviso-version-nueva" role="status">
                    <span>Hay una versión nueva de TransferisOK</span>
                    <button type="button" onClick={recargarVersionNueva}>Actualizar</button>
                </div>
            )}

            <div className="transactions-container">
                <TransactionsList
                    todayTransactions={todayTransactions}
                    yesterdayTransactions={yesterdayTransactions}
                    searchValue={searchValue}
                    onSearchChange={handleSearch}
                    highlightedTransactions={highlightedTransactions}
                    limite={limiteMonto}
                    alias={aliasCuenta}
                    titular={titularCuenta}
                    mostrarAlias={mostrarAlias}
                    onBusqueda={handleBusqueda}
                    onCambioAlias={handleCambioAlias}
                />
            </div>

            <OperatorsModal
                isOpen={showOperatorsModal}
                onClose={() => setShowOperatorsModal(false)}
                currentUser={usuarioMostrado}
                verComo={verComoActivo || undefined}
                operadoresFijos={verComoActivo ? operadoresVista : undefined}
                onCambios={cargarNegocios}
                onBaja={setBajaPendiente}
                limiteCuenta={verComoActivo ? limiteCuentaVista : undefined}
            />

            <ProfileModal
                isOpen={showProfileModal}
                onClose={() => setShowProfileModal(false)}
                currentUser={usuarioMostrado}
                onLogout={handleLogoutAttempt}
                alias={aliasCuenta}
                mostrarAlias={mostrarAlias}
                onGuardarAlias={(isAdmin || isPrimaryAdmin) && !verComoActivo ? guardarAliasDeLaCuenta : undefined}
                onMostrarAlias={(isAdmin || isPrimaryAdmin) && !verComoActivo ? cambiarMostrarAlias : undefined}
            />

            <WelcomeAdminModal
                isOpen={showWelcomeModal}
                onClose={() => setShowWelcomeModal(false)}
                onOpenOperatorsModal={() => setShowOperatorsModal(true)}
            />

            <ConfirmModal
                isOpen={showLogoutConfirm}
                onClose={() => setShowLogoutConfirm(false)}
                onConfirm={handleLogoutConfirm}
                message="¿Estás seguro que deseas cerrar sesión?"
            />

            {/* Las consultas de ayuda las manda siempre quien está logueado */}
            <HelpModal
                isOpen={showHelpModal}
                onClose={() => setShowHelpModal(false)}
            />

            <BajaUsuarioModal
                email={bajaPendiente}
                onClose={() => setBajaPendiente(null)}
                onBajaHecha={(emails) => {
                    if (emails.includes(verComoActivo)) cambiarVista(null);
                    cargarNegocios();
                }}
            />
        </div>
    );
}

export default Home;

// Para /tutorial-operadores, que arma las imágenes del mail con datos inventados.
export { Header, TransactionsList };

